import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Copy, Users, TrendingUp, UserMinus, LogOut } from "lucide-react";
import { RubyIcon } from "@/components/Icons";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import Avatar from "@/components/Avatar";

function CodeChip({ code }) {
  const copy = () => {
    navigator.clipboard?.writeText(code);
    toast.success("Invite code copied — share it with streamers!");
  };
  return (
    <button data-testid="agency-code-chip" onClick={copy}
      className="glass rounded-full px-3.5 py-1.5 flex items-center gap-2 text-xs font-bold text-white active:scale-95 transition-transform">
      <span className="tracking-[0.25em] gradient-text font-extrabold">{code}</span>
      <Copy size={12} className="text-zinc-400" />
    </button>
  );
}

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/agency/me").then(({ data: d }) => setData(d)).catch(() => setData({ agency: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const join = async () => {
    setBusy(true);
    try {
      await api.post("/agency/join", { invite_code: code });
      toast.success("Welcome to the agency! 🏢");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
    setBusy(false);
  };

  const leave = async () => {
    if (!window.confirm("Leave this agency?")) return;
    try {
      await api.post("/agency/leave");
      toast.success("You left the agency");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the agency?`)) return;
    try {
      await api.delete(`/agency/members/${m.id}`);
      toast.success(`${m.name} removed`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) {
    return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div>;
  }

  const ag = data.agency;

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-16" data-testid="agency-page">
      <button data-testid="agency-back" onClick={() => navigate(-1)} className="text-zinc-400 mb-5 flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>

      {!ag && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 text-center" data-testid="agency-join-card">
          <Building2 size={36} className="mx-auto text-pink-400 mb-3" />
          <h1 className="text-xl font-extrabold font-display">Join an Agency</h1>
          <p className="text-zinc-500 text-xs mt-1.5 mb-6">Agencies help streamers grow. Enter the 6-character invite code from your agency.</p>
          <input data-testid="agency-code-input" value={code} maxLength={6}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            className="w-full bg-[#131A2A] border border-white/10 rounded-2xl px-4 py-3.5 outline-none text-white text-center text-xl tracking-[0.4em] font-extrabold focus:border-violet-500 transition-colors placeholder:text-zinc-600 uppercase" />
          <button data-testid="agency-join-button" onClick={join} disabled={code.length !== 6 || busy}
            className="w-full gradient-primary text-white font-bold py-3.5 rounded-2xl mt-4 disabled:opacity-40 active:scale-[0.98] transition-transform">
            {busy ? "Joining…" : "Join Agency"}
          </button>
        </motion.div>
      )}

      {ag && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between mb-1">
            <div>
              <p className="text-[10px] font-bold text-pink-400 flex items-center gap-1"><Building2 size={11} /> AGENCY {data.role === "owner" ? "· YOU ARE THE OWNER 👑" : ""}</p>
              <h1 className="text-2xl font-extrabold font-display" data-testid="agency-name">{ag.name}</h1>
              <p className="text-zinc-500 text-[10px] mt-0.5">Owner: {ag.owner?.name} · {ag.members_count} member{ag.members_count !== 1 ? "s" : ""} · {ag.commission_pct}% commission</p>
            </div>
            <CodeChip code={ag.invite_code} />
          </div>
          {ag.status !== "active" && (
            <p className="text-amber-400 text-xs font-bold mt-2" data-testid="agency-suspended-note">⚠️ This agency is suspended — commissions are paused.</p>
          )}

          {data.role === "owner" ? (
            <>
              <div className="grid grid-cols-3 gap-2 mt-5">
                <div className="glass rounded-2xl p-3 text-center" data-testid="agency-stat-members">
                  <Users size={14} className="mx-auto text-violet-400 mb-1" />
                  <p className="text-lg font-extrabold text-white">{ag.members_count}</p>
                  <p className="text-[9px] text-zinc-500">Members</p>
                </div>
                <div className="glass rounded-2xl p-3 text-center" data-testid="agency-stat-week">
                  <TrendingUp size={14} className="mx-auto text-emerald-400 mb-1" />
                  <p className="text-lg font-extrabold text-rose-300 flex items-center justify-center gap-1"><RubyIcon size={14} /> {ag.week_commission.toLocaleString()}</p>
                  <p className="text-[9px] text-zinc-500">Commission · 7d</p>
                </div>
                <div className="glass rounded-2xl p-3 text-center" data-testid="agency-stat-total">
                  <RubyIcon size={14} className="mx-auto mb-1" />
                  <p className="text-lg font-extrabold text-rose-300 flex items-center justify-center gap-1"><RubyIcon size={14} /> {ag.total_commission.toLocaleString()}</p>
                  <p className="text-[9px] text-zinc-500">Lifetime</p>
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2">Commission rubies are added to your wallet — withdraw them anytime from Wallet → Earnings.</p>

              <h2 className="text-sm font-extrabold font-display mt-6 mb-2">Roster</h2>
              <div className="space-y-2" data-testid="agency-roster">
                {data.members?.map((m) => (
                  <div key={m.id} className="glass rounded-2xl px-3 py-2.5 flex items-center justify-between" data-testid={`agency-member-${m.id}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar src={m.avatar} size={36} tier={m.tier} />
                      <div className="min-w-0">
                        <p className="text-white text-xs font-bold truncate">{m.name} {m.is_owner && "👑"}</p>
                        <p className="text-[9px] text-zinc-500 flex items-center gap-1">Lv.{m.level} · <RubyIcon size={9} /> {m.diamonds_week.toLocaleString()} this week · {m.diamonds_total.toLocaleString()} total</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-[9px] text-emerald-400 font-bold flex items-center gap-0.5">+{m.commission_total.toLocaleString()} <RubyIcon size={9} /></span>
                      {!m.is_owner && (
                        <button data-testid={`agency-remove-${m.id}`} onClick={() => removeMember(m)}
                          className="p-1.5 rounded-lg bg-white/10 text-zinc-500 hover:text-red-400"><UserMinus size={13} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="glass rounded-2xl p-4 mt-5" data-testid="agency-my-stats">
                <p className="text-[10px] text-zinc-500 font-semibold mb-1">MY CONTRIBUTION</p>
                <p className="text-xl font-extrabold text-rose-300 flex items-center gap-1.5"><RubyIcon size={18} /> {(data.my_stats?.diamonds_total || 0).toLocaleString()}</p>
                <p className="text-[10px] text-zinc-500">rubies earned since joining</p>
              </div>
              <button data-testid="agency-leave-button" onClick={leave}
                className="mt-6 w-full bg-white/10 border border-white/10 text-zinc-400 text-xs font-bold py-3 rounded-2xl flex items-center justify-center gap-1.5">
                <LogOut size={13} /> Leave Agency
              </button>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}
